import './ShareHistoryItem.scss';

import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';

import DefaultButton from '@components/Button/button';
import { Sanitize } from '@components/Sanitize';

import { SharingHistoryItem } from '../ShareResultsForm/ShareResults.types';

interface ShareHistoryItemProps {
  item: SharingHistoryItem;
  isUpToDate: boolean;
  sendingUpdateIds: Set<string>;
  onSendUpdatedResults: (item: SharingHistoryItem) => void;
  shouldAnimate?: boolean;
  animationKey?: number;
}

/**
 * Format ISO date string to "MM/DD/YY" format for display
 */
const formatDateForDisplay = (isoDateString: string): string => {
  const date = new Date(isoDateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

const ShareHistoryItem = ({
  item,
  isUpToDate,
  sendingUpdateIds,
  onSendUpdatedResults,
  shouldAnimate = false,
  animationKey = 0,
}: ShareHistoryItemProps) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (shouldAnimate) {
      // Reset to false first to allow animation to restart
      setIsAnimating(false);

      // Use a small delay to ensure the reset is processed, then start animation
      const resetTimer = setTimeout(() => {
        setIsAnimating(true);
        timerRef.current = setTimeout(() => {
          setIsAnimating(false);
          timerRef.current = null;
        }, 2000); // Animation duration
      }, 10);

      return () => {
        clearTimeout(resetTimer);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    } else {
      setIsAnimating(false);
    }
  }, [shouldAnimate, animationKey]); // Include animationKey to force re-trigger

  const itemId = item.id || item.email; // Fallback to email if id doesn't exist
  const isSending = sendingUpdateIds.has(itemId);
  const formattedDate = formatDateForDisplay(item.created_at);

  // Check if 24 hours or more have passed since the item was created
  // dayjs.diff() compares absolute timestamps, so timezone doesn't affect the hour calculation
  const canReshare = isUpToDate && dayjs().diff(dayjs(item.created_at), 'hour') >= 24;

  return (
    <div className={`share-history-item ${isAnimating ? 'updated' : ''}`}>
      <div className='me-2'>
        <Sanitize as='p' className='mb-0 fs-sm'>
          {item.email}
        </Sanitize>
        {item.name && (
          <Sanitize as='p' className='mb-0 fs-xs text-secondary-light fw-semibold'>
            {item.name}
          </Sanitize>
        )}
        {isUpToDate ? (
          <p className='mb-0 fs-xs text-secondary-light'>
            Last shared {formattedDate}
            {canReshare && (
              <>
                {' - '}
                <button
                  type='button'
                  className='btn btn-link p-0 text-primary text-decoration-none fs-xs fw-semibold border-0'
                  onClick={() => onSendUpdatedResults(item)}
                  disabled={isSending}
                  data-testid={`reshare-${itemId}`}
                  style={{ verticalAlign: 'baseline' }}
                >
                  {isSending ? 'Sharing...' : 'Reshare'}
                </button>
              </>
            )}
          </p>
        ) : (
          <p className='mb-2 fs-xs text-secondary-light'>Last shared {formattedDate}</p>
        )}
      </div>
      {!isUpToDate && (
        <DefaultButton
          type='button'
          buttonLabel={isSending ? 'Sharing...' : 'Share Updated Results'}
          styleClass='primaryButtonStyling'
          buttonStyling={{ fontSize: '12px', padding: '4px 8px' }}
          disabled={isSending}
          onClick={() => onSendUpdatedResults(item)}
          dataTestId={`sendUpdatedResults-${itemId}`}
        />
      )}
    </div>
  );
};

export default ShareHistoryItem;
